import React from 'react';

import { margin } from '@lumapps/classnames';
import { useGetConnectorAuthStatus } from '@lumapps/connectors/hooks/useGetConnectorAuthStatus';
import { useConnectorList } from '@lumapps/integration-connectors/hooks/useConnectorList';
import { useProviderList } from '@lumapps/integration-connectors/hooks/useProviderList';
import type { Connector } from '@lumapps/integrations/types';
import { ActionableCard } from '@lumapps/lumx-cards/components/ActionableCard';
import { mdiCheck } from '@lumapps/lumx/icons';
import { ColorPalette, Flag, Orientation } from '@lumapps/lumx/react';
import { useResponsive } from '@lumapps/responsive';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { capitalizeFirstLetter } from '@lumapps/utils/string/capitalizeFirstLetter';

import { AiAuthorizationError } from '../../types';

interface AiAuthorizationErrorCardProps {
    /** The connector needing authorization */
    connector: AiAuthorizationError['sourceConnector'];
    /** Function to update the errored connector's authorization status */
    setErroredConnectors: React.Dispatch<
        React.SetStateAction<
            {
                id: string;
                isConnected: boolean;
            }[]
        >
    >;
    /**
     * Whether the error card is in connected state
     * This will always be false at first, as, if it's displayed, it means the backend considers there is an error
     */
    isConnected: boolean;
}

/**
 * Component that displays an actionable card for a connector that requires authorization.
 * The card shows the connector's name, its provider's name and icon (if available),
 * and an action button to initiate the OAuth connection flow.
 * The card has a "connected" state that displays an informative flag.
 *
 * @param {AiAuthorizationErrorCardProps} props - Props for the component.
 * @returns {React.JSX.Element} - The rendered `AiAuthorizationErrorCard` component.
 */
export const AiAuthorizationErrorCard: React.FC<AiAuthorizationErrorCardProps> = ({
    connector,
    setErroredConnectors,
    isConnected,
}) => {
    const { translateKey } = useTranslate();

    /** Authorization status of the errored connector */
    const { refetch } = useGetConnectorAuthStatus({ connectorId: connector.id });

    const { isConnecting, handleConnectorConnect } = useConnectorList({
        initFetchConnectors: false,
        closeAuthWindowCallback: async () => {
            const { data: newAuthStatus } = await refetch();
            setErroredConnectors(
                (prevErroredConnectors) =>
                    prevErroredConnectors?.map((c) =>
                        c.id === connector.id ? { ...c, isConnected: newAuthStatus?.connected ?? false } : c,
                    ),
            );
        },
    });

    const { isSmall } = useResponsive();
    const orientation: Orientation = isSmall ? 'vertical' : 'horizontal';

    const { getProvider } = useProviderList();
    const provider = getProvider(connector.providerType);

    return (
        <ActionableCard
            as="li"
            color={ColorPalette.light}
            title={connector.name}
            afterTitle={
                isConnected && (
                    <Flag
                        color={ColorPalette.green}
                        label={translateKey(GLOBAL.CONNECTED)}
                        className={margin('left')}
                        icon={mdiCheck}
                        truncate
                    />
                )
            }
            subtitle={{
                subtitle: capitalizeFirstLetter(provider ? provider.name : ''),
                img: provider
                    ? {
                          image: provider.icon,
                          alt: '',
                          aspectRatio: 'square',
                          objectFit: 'contain',
                      }
                    : undefined,
            }}
            action={{
                style: { alignSelf: 'center' },
                children: isConnected ? translateKey(GLOBAL.CONNECTED) : translateKey(GLOBAL.CONNECT),
                isDisabled: isConnecting || isConnected,
                onClick: () => handleConnectorConnect({ id: connector.id } as Connector),
            }}
            wrapperProps={{ gap: 'big', orientation }}
            className={margin('top', 'regular')}
        />
    );
};
